import { Observable } from "rxjs/Observable";
import { EndpointOptions, EndpointService } from "./endpoint.service";
import { ResponseWrapper } from "./response-wrapper";

export abstract class BaseDao<T> {
  /**
   * Base Dao class designed to simplify the creation of simple
   * entity based CRUD RESTFul operations.
   *
   * Note:
   * - `path` is basically url without base_url.  E.g.: path
   *   '/d/notice/' path is '/notice/'.
   * - Override `_base_url` to '' essentially set path to be url
   * - public methods allow override entity used.  protected method
   *   allow setting arbitrage paths
   */

  protected readonly _base_url = "/s"; // URL to web api
  private _entity = "";
  private _secure = true;

  constructor(protected endpoint: EndpointService) {}

  request(entity?: string) {
    entity = this.getEntity(entity);
    const url = this.getUrl(entity, null, false);

    return this.handle(this.endpoint.getOne(url, this.options()));
  }

  query(entity?: string): Observable<T> {
    entity = this.getEntity(entity);
    const url = this.getUrl(entity);

    return this.handle(this.endpoint.getOne(url, this.options()));
  }

  getList(key: string, entity?: string): Observable<T[]> {
    entity = this.getEntity(entity);
    const url = this.getUrl(entity, key);

    return this.handle(this.endpoint.getMany(url, this.options()));
  }

  get(key: string, entity?: string): Observable<T> {
    entity = this.getEntity(entity);
    const url = this.getUrl(entity, key);

    return this.handle(this.endpoint.getOne(url, this.options()));
  }

  put(key: string, data: any, entity?: string): Observable<T> {
    entity = this.getEntity(entity);
    const url = this.getUrl(entity, key);
    return this.handle(this.endpoint.put(url, data, this.options()));
  }

  post(data: any, entity?: string): Observable<T> {
    entity = this.getEntity(entity);
    const url = this.getUrl(entity);

    return this.handle(this.endpoint.post(url, data, this.options()));
  }

  delete(key: string, entity?: string): Observable<T> {
    entity = this.getEntity(entity);
    const url = this.getUrl(entity, key);

    return this.handle(this.endpoint.delete(url, this.options()));
  }

  protected handle<Y>(request: Observable<ResponseWrapper<Y>>): Observable<Y> {
    return request
      .map((wrapper) => wrapper.data as Y)
      .catch((e) => this.handleDataError(e));
  }

  protected configure(entity: string, secure = true) {
    this._entity = entity;
    this._secure = secure;
  }

  protected handleDataError(error: any) {
    console.error("An error occurred", error); // for demo purposes only

    return Observable.throw(error.message || error);
  }

  protected getUrl(path: string, key?: string, trailingSlash = true) {
    const preSeparator = path.startsWith("/") ? "" : "/";
    const postSeparator = !trailingSlash || path.endsWith("/") ? "" : "/";
    let url = this._base_url + preSeparator + path + postSeparator;
    if (key) {
      url += key;
    }

    return url;
  }

  protected getEntity(entity: string) {
    return entity ? entity : this._entity;
  }

  protected options(): EndpointOptions {
    return {
      secure: this._secure,
      mapper: (data) => data,
      resource: false,
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/core/dao.module.ts